import { isFunction } from 'lodash';
import { useCallback } from 'react';

import { Passport, Pesel } from '@dreryk/personal-external-identifier-common-api';

import { assertUnreachable, Bean, FieldControllerProps, useTranslate, ValidationRules } from '@dreryk/react-common';
import { Select } from '@dreryk/react-components';

import { medicalLeaveTranslationKeys } from '../../../../../../translations';

export type GuardianExternalIdentifierType = typeof Pesel.CLASS | typeof Passport.CLASS;

const GUARDIAN_EXTERNAL_IDENTIFIER_TYPES: GuardianExternalIdentifierType[] = [Pesel.CLASS, Passport.CLASS];

export type GuardianExternalIdentifierTypeVisitor = <ACCEPT>(
   visitor:
      | ((type: GuardianExternalIdentifierType) => ACCEPT)
      | Record<`visit${GuardianExternalIdentifierType}`, () => ACCEPT>
) => ACCEPT;

export const createGuardianExternalIdentifierTypeVisitor = (
   guardianExternalIdentifierType: GuardianExternalIdentifierType
): GuardianExternalIdentifierTypeVisitor => (visitor) => {
   if (isFunction(visitor)) {
      return visitor(guardianExternalIdentifierType);
   }

   switch (guardianExternalIdentifierType) {
      case 'Pesel':
         return visitor.visitPesel();
      case 'Passport':
         return visitor.visitPassport();
      default:
         return assertUnreachable(guardianExternalIdentifierType);
   }
};

const VALIDATION_RULES: ValidationRules<Bean> = {
   required: true,
};

const useTriggerLabelExtractor = (): ((type?: GuardianExternalIdentifierType | null) => string) => {
   const translate = useTranslate();
   return useCallback(
      (type) => {
         return type ? translate(medicalLeaveTranslationKeys.personalData.identifierType.typeName[type]) : '';
      },
      [translate]
   );
};

export type OnGuardianExternalIdentifierTypeSelect = (type: GuardianExternalIdentifierType | null) => void;

type GuardianExternalIdentifierTypeFieldProps = FieldControllerProps<
   'guardianExternalIdentifierType',
   GuardianExternalIdentifierType
> & {
   readonly onSelect: OnGuardianExternalIdentifierTypeSelect;
};

export const GuardianExternalIdentifierTypeField = (props: GuardianExternalIdentifierTypeFieldProps): JSX.Element => {
   return (
      <Select<GuardianExternalIdentifierType>
         testId={'guardianExternalIdentifierType'}
         label={medicalLeaveTranslationKeys.personalData.identifierType.label}
         triggerLabelExtractor={useTriggerLabelExtractor()}
         controller={props.register('guardianExternalIdentifierType', VALIDATION_RULES)}
         defaultValue={'Pesel'}
         onSelect={props.onSelect}
      >
         <Select.List<GuardianExternalIdentifierType> items={GUARDIAN_EXTERNAL_IDENTIFIER_TYPES}>
            {(identifierType) => (
               <Select.Item
                  testId={identifierType}
                  item={identifierType}
                  label={medicalLeaveTranslationKeys.personalData.identifierType.typeName[identifierType]}
               />
            )}
         </Select.List>
      </Select>
   );
};
